import type { NormalizedHint, PayloadShape } from "./shared";

/** Readonly-секция «Связанный regulatory case» для enforcement_candidate.
 *  Группирует ru_term_hints по label, добавляет ссылку на источник
 *  case'а (title / source_ref / source_url) если есть в payload.case_matches. */
export function RegulatoryCaseBlock({
  hints,
  payload,
}: {
  hints: NormalizedHint[];
  payload: PayloadShape;
}) {
  const byLabel = new Map<string, NormalizedHint[]>();
  for (const h of hints) {
    const k = h.label ?? "—";
    if (!byLabel.has(k)) byLabel.set(k, []);
    byLabel.get(k)!.push(h);
  }
  const caseId = hints.find((h) => h.caseId)?.caseId;
  const caseMatches = payload.case_matches ?? [];
  const matchedCase = caseMatches.find(
    (cm) => caseId && (cm.case_id as string) === caseId,
  );
  const caseTitle = matchedCase?.title as string | undefined;
  const sourceRef = matchedCase?.source_ref as string | undefined;
  const sourceUrl = matchedCase?.source_url as string | undefined;

  return (
    <section className="rf-section">
      <div className="rf-section__head">
        <span>Связанный regulatory case</span>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        {caseId && (
          <span
            className="mt-text-tight mt-muted mt-mono"
            style={{ textTransform: "none", letterSpacing: 0 }}
          >
            {caseId}
          </span>
        )}
      </div>
      <div className="rf-section__body">
        {(caseTitle || sourceRef) && (
          <div
            className="mt-text-tight mt-muted"
            style={{
              marginBottom: 10,
              textTransform: "none",
              letterSpacing: 0,
            }}
          >
            {caseTitle && (
              <strong style={{ color: "var(--ink)" }}>{caseTitle}</strong>
            )}
            {caseTitle && sourceRef && " · "}
            {sourceRef && <span>{sourceRef}</span>}
            {sourceUrl && (
              <>
                {" · "}
                <a href={sourceUrl} target="_blank" rel="noreferrer noopener">
                  источник
                </a>
              </>
            )}
          </div>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(180px, max-content) 1fr",
            gap: "6px 14px",
            fontSize: "var(--fs-sm)",
          }}
        >
          {Array.from(byLabel.entries()).map(([label, group]) => (
            <FragmentRow key={label} label={label} group={group} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FragmentRow({
  label,
  group,
}: {
  label: string;
  group: NormalizedHint[];
}) {
  const sorted = [...group].sort((a, b) => {
    const score = (h: NormalizedHint) => (h.hintType === "preferred" ? 0 : 1);
    return score(a) - score(b);
  });
  return (
    <>
      <div
        className="mt-mono mt-text-tight"
        style={{ color: "var(--ink)", fontSize: "var(--fs-xs)" }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {sorted.map((h, i) => (
          <div
            key={i}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <span
              className={
                "mt-badge mt-badge--no-dot " +
                (h.hintType === "preferred"
                  ? "mt-badge--success"
                  : "mt-badge--neutral")
              }
            >
              {h.hintType ?? "—"}
            </span>
            <span>{h.text}</span>
          </div>
        ))}
      </div>
    </>
  );
}
